import React, { useState, useEffect, useRef } from "react";
import { Input } from "@/components/atoms/Input";
import { Label } from "@/components/atoms/Label";
import type { BaseFieldProps, FieldConfig } from "./types";

interface RGBAFieldProps extends BaseFieldProps {
  field: FieldConfig;
}

const RGBAField: React.FC<RGBAFieldProps> = ({
  fieldName,
  label,
  placeholder,
  disabled,
  className,
  required,
  hasError,
  validationMessage,
  formData,
  handleChange,
  field,
}) => {
  // Parse current rgba value
  const parseRgba = (rgba: string) => {
    const match = rgba.match(
      /rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/
    );
    if (match) {
      const r = parseInt(match[1]);
      const g = parseInt(match[2]);
      const b = parseInt(match[3]);
      const a = match[4] ? parseFloat(match[4]) : 1;
      const hex = `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
      return { hex, r, g, b, alpha: a };
    }
    return { hex: "#000000", r: 0, g: 0, b: 0, alpha: 1 };
  };

  /** A complete, committable colour. Partial input never reaches formData. */
  const HEX_RE = /^#[0-9A-Fa-f]{6}$/;

  // Use a ref to track the previous value to prevent unnecessary updates
  const prevValueRef = useRef<string | undefined>(formData[fieldName]);

  const [color, setColor] = useState(() =>
    parseRgba(formData[fieldName] || "rgba(0, 0, 0, 1)")
  );

  /**
   * Text-box draft, tracked separately from `color`. Typing a partial hex
   * (`#03`) or clearing the box used to be written straight through
   * `hexToRgba`, whose `parseInt` on the missing pairs produced
   * "rgba(NaN, NaN, NaN, 1.00)". That string fails `parseRgba`'s regex, so the
   * sync effect below fell back to #000000 and the field appeared to refuse
   * being cleared. Keeping a draft lets the box hold anything mid-edit while
   * only complete values are committed.
   */
  const [hexDraft, setHexDraft] = useState<string>(() =>
    formData[fieldName] ? parseRgba(formData[fieldName]).hex : ""
  );

  // Update when formData changes - but only if the value actually changed
  useEffect(() => {
    const currentValue = formData[fieldName];
    // Only update if the value actually changed (not just a new object reference)
    if (currentValue !== prevValueRef.current) {
      prevValueRef.current = currentValue;
      if (currentValue) {
        const parsedColor = parseRgba(currentValue);
        setColor(parsedColor);
        setHexDraft(parsedColor.hex);
      } else {
        setColor(parseRgba("rgba(0, 0, 0, 1)"));
        setHexDraft("");
      }
    }
  }, [formData, fieldName]);

  /** Returns "" for an incomplete hex rather than an rgba() full of NaN. */
  const hexToRgba = (hex: string, alpha: number) => {
    if (!HEX_RE.test(hex)) return "";
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha.toFixed(2)})`;
  };

  const handleColorChange = (newHex: string) => {
    if (!HEX_RE.test(newHex)) return;
    const r = parseInt(newHex.slice(1, 3), 16);
    const g = parseInt(newHex.slice(3, 5), 16);
    const b = parseInt(newHex.slice(5, 7), 16);
    setColor({ ...color, hex: newHex, r, g, b });
    setHexDraft(newHex);
    handleChange(fieldName, hexToRgba(newHex, color.alpha));
  };

  /**
   * Free typing in the hex box. An empty box clears the stored value (the
   * widget then falls back to its own default); anything shorter than a full
   * hex is held in the draft until it is complete.
   */
  const handleHexInput = (value: string) => {
    setHexDraft(value);
    if (value === "") {
      handleChange(fieldName, "");
      return;
    }
    if (HEX_RE.test(value)) handleColorChange(value);
  };

  /** Discard a half-typed hex on blur, but leave a deliberate clear alone. */
  const handleHexBlur = () => {
    if (hexDraft !== "" && !HEX_RE.test(hexDraft)) setHexDraft(color.hex);
  };

  const handleAlphaChange = (newAlpha: number) => {
    setColor({ ...color, alpha: newAlpha });
    const next = hexToRgba(color.hex, newAlpha);
    if (next) handleChange(fieldName, next);
  };

  return (
    <div key={fieldName} className="flex-1 min-w-80 max-w-md">
      {label && (
        <Label
          htmlFor={fieldName}
          className={`peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-sm font-medium ${field.labelClassName || ""}`}
        >
          {label}
          {field.showAsterisk !== false && required && (
            <span className="text-secondary-main ml-1">*</span>
          )}
        </Label>
      )}
      <div className="space-y-3 mt-1">
        {/* Color Picker Row */}
        <div className="flex gap-2 items-center">
          <Input
            type="color"
            value={color.hex}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleColorChange(e.target.value)
            }
            disabled={disabled}
            classType="admin"
            className={`w-20 h-10 p-1 cursor-pointer ${className || ""} ${hasError ? "border-secondary-main" : ""}`}
          />
          <Input
            type="text"
            value={hexDraft}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const value = e.target.value;
              if (value === "" || /^#[0-9A-Fa-f]{0,6}$/.test(value)) {
                handleHexInput(value);
              }
            }}
            onBlur={handleHexBlur}
            placeholder="#000000"
            disabled={disabled}
            classType="admin"
            className={`flex-1 text-base md:text-sm ${className || ""} ${hasError ? "border-secondary-main" : ""}`}
          />
        </div>

        {/* Opacity Slider */}
        <div className="space-y-1">
          <div className="flex justify-between text-xs text-gray-600">
            <span>Opacity</span>
            <span>{Math.round(color.alpha * 100)}%</span>
          </div>
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={color.alpha}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleAlphaChange(parseFloat(e.target.value))
            }
            disabled={disabled}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary-main"
          />
        </div>

        {/* RGBA Output Display */}
        <div className="flex gap-2 items-center">
          <Input
            type="text"
            value={formData[fieldName] || ""}
            readOnly
            disabled={disabled}
            classType="admin"
            className={`flex-1 text-base md:text-sm bg-gray-50 ${className || ""} ${hasError ? "border-secondary-main" : ""}`}
          />
          {/* Color Preview */}
          <div
            className="w-10 h-10 rounded border border-gray-300"
            style={{
              backgroundColor: formData[fieldName] || "rgba(0, 0, 0, 1)",
            }}
          />
        </div>
      </div>
      {hasError && (
        <p className="text-secondary-main text-body-4-medium mt-1">
          {validationMessage || "This field is required"}
        </p>
      )}
    </div>
  );
};

export default RGBAField;


